import React, { Component, Fragment } from 'react';
import { computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import { find, unionBy } from 'lodash';

import ClientsClass from '../../clients/ClientsClass';
import { AppConstants } from '../../constants';
import { IFunderStaff, ILoanAccount } from '../../interfaces';
import { FunderStoreClass, SessionStoreClass } from '../../stores';
import FormattingUtils from '../../utils/FormattingUtils';
import RepresentativeDropdownAssign from '../page-case/RepresentativeDropdownAssign';

import { ILoanAccountFormData } from './leadSource';

const UnTypedRepresentativeDropdownAssign: any = RepresentativeDropdownAssign;

const OWNER_FIELDS: Array<keyof ILoanAccount> = [
  'primary_representative',
  'secondary_representative',
  'tertiary_representative',
];

const { EMPTY_FIELD, PERMISSIONS } = AppConstants
  , { getNameOrDefault } = FormattingUtils
  ;

interface IProps {
  loanAccount: ILoanAccount;
  // Assigning writes an OWNER_ASSIGNED activity, so the feed is re-read once the save lands
  onOwnerChanged: () => Promise<void>;
  onSave: (data: ILoanAccountFormData) => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  FunderStore: FunderStoreClass;
  SessionStore: SessionStoreClass;
}

/**
 * The three staff who look after the account, assigned a position at a time from the page header.
 * c.f. CaseDetailHeaderAssignments, whose dropdowns these are; a case calls the same three people
 * its representatives, which is what the API calls these too.
 */
@inject('Clients', 'FunderStore', 'SessionStore')
@autoBindMethods
@observer
class LoanAccountHeaderOwners extends Component<IProps> {
  // Only staff who can see a loan account are worth putting on one, so the menus offer the funder's
  // staff holding that permission rather than all of them.
  @observable private permittedStaff: IFunderStaff[] = [];

  public async componentDidMount () {
    // Nothing to offer a user who cannot assign, so the request is not made either
    if (!this.canAssign) { return; }

    const { Clients } = this.injected;

    try {
      this.permittedStaff = await Clients.funderStaff.list({
        params: { permission: PERMISSIONS.VIEW_LOAN_ACCOUNT },
      });
    }
    catch (error) {
      // Transport has already toasted the failure. The menus fall back to naming whoever the account
      // already has, which beats a header that cannot say who owns it.
      // tslint:disable-next-line no-console
      console.error('Error loading the staff an account can be assigned to:', error);
    }
  }

  private get injected () {
    return this.props as IInjected;
  }

  /**
   * The endpoint rejects a PATCH from a user without the change permission, so a view-only user gets
   * the owners as text rather than three menus that cannot save. c.f. TabDetails and its edit pencil.
   */
  private get canAssign () {
    return this.injected.SessionStore.userHasPermission(PERMISSIONS.CHANGE_LOAN_ACCOUNT);
  }

  private get ownerIds () {
    const { loanAccount } = this.props;

    return OWNER_FIELDS.map(field => loanAccount[field]);
  }

  /**
   * Whoever already holds a position stays on the list whether they hold the permission or not, so
   * that the header can still name them. The permission gates assigning, not who is on an account.
   */
  @computed
  private get assignableStaff () {
    const { FunderStore } = this.injected
      , currentOwners = FunderStore.representatives.filter(staff => this.ownerIds.includes(staff.id))
      ;

    return unionBy(this.permittedStaff, currentOwners, 'id');
  }

  private ownerName (ownerId: null | string) {
    const owner = find(this.assignableStaff, { id: ownerId as string });

    return owner ? getNameOrDefault(owner) : EMPTY_FIELD;
  }

  private async onOwnerChanged (field: keyof ILoanAccount, owner: null | string) {
    await this.props.onSave({ [field]: owner });
    await this.props.onOwnerChanged();
  }

  private async onPrimaryOwnerChanged (owner: null | string) {
    await this.onOwnerChanged('primary_representative', owner);
  }

  private async onSecondaryOwnerChanged (owner: null | string) {
    await this.onOwnerChanged('secondary_representative', owner);
  }

  private async onTertiaryOwnerChanged (owner: null | string) {
    await this.onOwnerChanged('tertiary_representative', owner);
  }

  private renderOwner (label: string, field: keyof ILoanAccount, onChange: (owner: null | string) => Promise<void>) {
    const { loanAccount } = this.props
      , selected = loanAccount[field] as null | string
      // One person holds one position, so the other two are off the menu
      , omit = OWNER_FIELDS.filter(ownerField => ownerField !== field).map(ownerField => loanAccount[ownerField])
      ;

    return (
      <dl>
        <dt>{label}</dt>
        <dd>
          {this.canAssign
            ? (
              <UnTypedRepresentativeDropdownAssign
                omit={omit}
                onChange={onChange}
                representatives={this.assignableStaff}
                selected={selected}
              />
            )
            : <span>{this.ownerName(selected)}</span>
          }
        </dd>
      </dl>
    );
  }

  public render () {
    return (
      <Fragment>
        {this.renderOwner('Primary Owner', 'primary_representative', this.onPrimaryOwnerChanged)}
        {this.renderOwner('Secondary Owner', 'secondary_representative', this.onSecondaryOwnerChanged)}
        {this.renderOwner('Tertiary Owner', 'tertiary_representative', this.onTertiaryOwnerChanged)}
      </Fragment>
    );
  }
}

export default LoanAccountHeaderOwners;
